import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlass, X, WarningCircle, User, Buildings, House } from '@phosphor-icons/react';
import { globalSearch } from '../lib/api';
import { Input } from './ui/input';

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
}

interface SearchResults {
  issues?: Array<{ id: string; title?: string; description?: string }>;
  users?: Array<{ id: string; name?: string; email?: string }>;
  residents?: Array<{ id: string; name?: string; email?: string }>;
  tenants?: Array<{ id: string; name?: string; email?: string }>;
  properties?: Array<{ id: string; name?: string; address?: string }>;
  apartments?: Array<{ id: string; apartment_number?: string }>;
}

export function SearchModal({ open, onClose }: SearchModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults(null);
      return;
    }
    
    setLoading(true);
    try {
      const { data } = await globalSearch(searchQuery);
      setResults(data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(query);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [query, handleSearch]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (open) {
          onClose();
        }
      }
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults(null);
    }
  }, [open]);

  const handleSelect = (type: string, id: string) => {
    onClose();
    switch (type) {
      case 'issue':
        navigate(`/issues/${id}`);
        break;
      case 'user':
        navigate(`/residents/${id}`);
        break;
      case 'property':
        navigate(`/properties/${id}`);
        break;
      case 'apartment':
        navigate(`/apartments/${id}`);
        break;
      default:
        break;
    }
  };

  if (!open) return null;

  const hasResults = results && (
    (results.issues?.length ?? 0) > 0 ||
    (results.users?.length ?? 0) > 0 ||
    (results.properties?.length ?? 0) > 0 ||
    (results.apartments?.length ?? 0) > 0
  );

  return (
    <>
      <div className="search-overlay" onClick={onClose} />
      <div className="search-modal animate-fade-in" data-testid="search-modal">
        <div className="p-4 border-b border-border">
          <div className="relative">
            <MagnifyingGlass 
              size={20} 
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
            />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('search_placeholder')}
              className="pl-10 pr-10"
              data-testid="search-input"
            />
            <button
              onClick={onClose}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        <div className="max-h-96 overflow-y-auto scrollbar-thin">
          {loading && (
            <div className="p-8 text-center text-slate-500">
              {t('loading')}
            </div>
          )}
          
          {!loading && query && !hasResults && (
            <div className="p-8 text-center text-slate-500">
              {t('no_results')}
            </div>
          )}
          
          {!loading && hasResults && results && (
            <div className="p-2">
              {(results.issues?.length ?? 0) > 0 && (
                <div className="mb-4">
                  <div className="px-3 py-2 text-xs font-medium text-slate-500 uppercase">
                    {t('issues')}
                  </div>
                  {results.issues?.map((issue) => (
                    <button
                      key={issue.id}
                      onClick={() => handleSelect('issue', issue.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50 text-left"
                      data-testid={`search-result-issue-${issue.id}`}
                    >
                      <WarningCircle size={18} className="text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {issue.title}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {issue.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {(results.users?.length ?? 0) > 0 && (
                <div className="mb-4">
                  <div className="px-3 py-2 text-xs font-medium text-slate-500 uppercase">
                    {t('residents')}
                  </div>
                  {results.users?.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleSelect('user', user.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50 text-left"
                      data-testid={`search-result-user-${user.id}`}
                    >
                      <User size={18} className="text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {user.name}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {user.email}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {(results.properties?.length ?? 0) > 0 && (
                <div className="mb-4">
                  <div className="px-3 py-2 text-xs font-medium text-slate-500 uppercase">
                    {t('properties')}
                  </div>
                  {results.properties?.map((prop) => (
                    <button
                      key={prop.id}
                      onClick={() => handleSelect('property', prop.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50 text-left"
                      data-testid={`search-result-property-${prop.id}`}
                    >
                      <Buildings size={18} className="text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {prop.name}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {prop.address}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {(results.apartments?.length ?? 0) > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-medium text-slate-500 uppercase">
                    {t('apartments')}
                  </div>
                  {results.apartments?.map((apt) => (
                    <button
                      key={apt.id}
                      onClick={() => handleSelect('apartment', apt.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50 text-left"
                      data-testid={`search-result-apartment-${apt.id}`}
                    >
                      <House size={18} className="text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {t('apartment')} {apt.apartment_number}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
